import React, { forwardRef, useCallback, useEffect, useState } from 'react';

import { Box, Divider, Flex, Spinner } from '@chakra-ui/react';
import CommentSection from 'components/RabbitInitiativeSidePanel/Components/CommentSection';
import { useOKRAccessControl } from 'components/RabbitOKRSidePanel/AccessControl/useOKRAccessControl';
// eslint-disable-next-line import/no-cycle
import { CheckInSectionHeader } from 'components/RabbitOKRSidePanel/Components/SectionHeaders';
import { updateLocalCheckInStateValues } from 'components/RabbitOKRSidePanel/KRslice';
import { useOKRProgressCheckinMutation } from 'components/RabbitOKRSidePanel/datahooks/useOKRProgressCheckinMutation';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import useOKRConfig from 'components/hooks/useOKRConfig';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useToast } from 'components/hooks/useToast';
import { useSelector, useDispatch } from 'react-redux';
import { kRDrawerSelector } from 'selectors/kRDrawer';

import { InfoWorkZero } from 'assets/assetsV1/iconComponents';

import {
  OBJECTIVE_TYPES_MAP,
  OKR_PROGRESS_TYPES,
  OKR_TYPES,
  TASK_TYPES_2,
  TOAST_STATUSES,
} from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';
import { getHumanReadableDate } from 'constants/helper';
import { sendCheckInRecordedEvent, sendClickedOnCheckinEvent, sendCommentedOnKREvent } from 'constants/mixpanelHelper';

import { Button } from 'uiToolkitV1/Button';
import { TextLabel } from 'uiToolkitV1/Typography';

import { OKRAchievedValue } from '../Overview/OKRAchievedValue';
import { OKRProgressSlider } from '../Overview/OKRProgressSlider';
import OKRStatus from '../Overview/OKRStatus';

export const checkCheckInValuesModified = (localCheckInStateValues, okrDetails) => {
  const isCheckInValuesModified =
    (Object.keys(localCheckInStateValues).length > 0 &&
      localCheckInStateValues?.actual !== undefined &&
      okrDetails?.actual !== undefined &&
      +localCheckInStateValues.actual !== +okrDetails.actual) ||
    (localCheckInStateValues?.goalClosureStatus !== undefined &&
      localCheckInStateValues?.goalClosureStatus !== okrDetails?.goalClosureStatus) ||
    (localCheckInStateValues?.goalProgressLevel !== undefined &&
      localCheckInStateValues?.goalProgressLevel !== okrDetails?.goalProgressLevel);

  return isCheckInValuesModified;
};

const CheckIn = ({ isOKRPanelDisabled }) => {
  const dispatch = useDispatch();
  const okrDetails = useSelector(kRDrawerSelector);

  const {
    uuid,
    type,
    startingProgressValue,
    target,
    actual,
    avgProgressPercentage,
    goalClosureStatus,
    goalProgressLevel,
    measure,
    measureType,
    progressType,
    localCheckInStateValues,
    lastCheckInTS,
  } = okrDetails;

  const { mutate: checkinMutate } = useOKRProgressCheckinMutation(uuid);
  const toast = useToast();
  const [addCommentFromOutsideToggle, setAddCommentFromOutsideToggle] = useState(false);
  // TODO: This is ugly and needs to be refactored
  const [isCommentUpdateInProgress, setIsCommentUpdateInProgress] = useState(false);

  const isCommentModified = localCheckInStateValues?.isCommentModified || false;

  const handleCheckIn = useCallback(() => {
    sendCheckInRecordedEvent({
      eventProperties: {
        goalId: uuid,
        page: type === OKR_TYPES.OBJECTIVE ? 'Edit Obj Panel' : 'Edit KR Panel',
      },
    });

    const localCheckInStateValuesCopy = { ...localCheckInStateValues };

    if (localCheckInStateValuesCopy.isCommentModified) {
      delete localCheckInStateValuesCopy.isCommentModified;
    }
    checkinMutate({
      updates: localCheckInStateValuesCopy,
    });
  }, [uuid, type, checkinMutate, localCheckInStateValues]);

  const handleLocalSave = useCallback(
    (updates) => {
      dispatch(updateLocalCheckInStateValues(updates));
    },
    [dispatch]
  );

  // Backend percentage calculation logic, don't change this
  const computeProgressPercentage = useCallback(() => {
    const localActual = localCheckInStateValues.actual || actual;
    const denominator = target - startingProgressValue;
    if (startingProgressValue === target && startingProgressValue !== 0) {
      const deviationPercentage = ((localActual - startingProgressValue) / target) * 100;
      return Math.min(100, Math.max(0, 100 - Math.abs(deviationPercentage)));
    }
    return denominator === 0 ? 0 : ((localActual - startingProgressValue) / (target - startingProgressValue)) * 100;
  }, [localCheckInStateValues.actual, actual, startingProgressValue, target]);

  useEffect(() => {
    // This is to handle the case of the first render
    if (localCheckInStateValues.actual === undefined) {
      handleLocalSave({ actual, goalClosureStatus, goalProgressLevel });
    }
  }, [actual, goalClosureStatus, goalProgressLevel, handleLocalSave, localCheckInStateValues.actual]);

  useEffect(() => {
    if (localCheckInStateValues.actual !== undefined) {
      const localPercentage = computeProgressPercentage(actual);
      handleLocalSave({ avgProgressPercentage: localPercentage });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localCheckInStateValues.actual]);

  const onCheckinClick = () => {
    sendClickedOnCheckinEvent({
      eventProperties: {
        goalId: uuid,
        page: type === OKR_TYPES.OBJECTIVE ? 'Edit Obj Panel' : 'Edit KR Panel',
      },
    });
    setAddCommentFromOutsideToggle(!addCommentFromOutsideToggle);

    if (checkCheckInValuesModified(localCheckInStateValues, okrDetails)) {
      handleCheckIn();
    }
  };

  const { canUpdateOKRStatusAndProgress } = useOKRAccessControl();

  /* 
    Following code is to handle okr config 
    Starts here
  */
  /* 
    changes as per objective type
    refer: https://coda.io/d/Q2-2023_doMnwta8vat/Objective-type-checklist_suwA2#Create-Edit-panel-settings-as-per-objective-type_tu5TD/r1 
  */

  const { OBJECTIVE_TYPE } = useOKRConfig();

  const isObjective = type === OKR_TYPES.OBJECTIVE;

  const isSimpleObjectiveType = isObjective && OBJECTIVE_TYPE === OBJECTIVE_TYPES_MAP.SIMPLE.value;

  const isCompactObjectiveType = isObjective && OBJECTIVE_TYPE === OBJECTIVE_TYPES_MAP.COMPACT.value;

  /* 
    Above code is to handle okr config 
    Ends here
  */

  /* based on selected tracking type, restrict user from updating achieved, target value */
  const isTrackedByChildren = [OKR_PROGRESS_TYPES.DERIVED_AVG, OKR_PROGRESS_TYPES.DERIVED_SUM].includes(progressType);

  /* is tracked via thirdPartyPlugin */
  const isTrackedByThirdPartyPlugin = [OKR_PROGRESS_TYPES.TRACKED].includes(progressType);

  /* Access controls for components based on privileges and config */

  const isStartAndTargetValueSame = startingProgressValue === target;
  const cannotEditAchieved =
    !canUpdateOKRStatusAndProgress ||
    isTrackedByChildren ||
    isSimpleObjectiveType ||
    isCompactObjectiveType ||
    isTrackedByThirdPartyPlugin;

  /* Show hide controls based on config */
  const { objectivesName, keyResultsName, moduleName } = useOrganisationSettings();
  const page = useViewPageName();

  /* achieved disable reason */
  let achievedReadOnlyReason = '';

  if (cannotEditAchieved) {
    if (!canUpdateOKRStatusAndProgress) {
      achievedReadOnlyReason = `You don't have access to update progress of this ${
        isObjective
          ? modifyString(objectivesName, StringModifier.SENTENCE_CASE)
          : modifyString(keyResultsName, StringModifier.SENTENCE_CASE)
      }`;
    } else if (isTrackedByThirdPartyPlugin) {
      achievedReadOnlyReason = `This ${
        isObjective
          ? modifyString(objectivesName, StringModifier.SENTENCE_CASE)
          : modifyString(keyResultsName, StringModifier.SENTENCE_CASE)
      }'s progress is tracked by a third party plugin.`;
    } else {
      achievedReadOnlyReason = `Progress of this ${
        isObjective
          ? modifyString(objectivesName, StringModifier.SENTENCE_CASE)
          : modifyString(keyResultsName, StringModifier.SENTENCE_CASE)
      } can only be updated through one of the child ${modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)}.`;
    }
  }

  const handleCommentSaved = useCallback(() => {
    toast({
      title: 'Your comment has been saved in the History section',
      status: TOAST_STATUSES.SUCCESS,
      position: 'bottom-left',
    });
    if (!isObjective) {
      sendCommentedOnKREvent({ eventProperties: { page, source: 'CheckIn Panel', goadId: uuid } });
    }
  }, [isObjective, page, toast, uuid]);

  return (
    <Flex flexDirection="column" alignItems="stretch" flexWrap="nowrap" w="100%">
      <CheckInSectionHeader />
      <Divider sx={{ borderColor: '#CFD7DF' }} marginBottom="12px" />

      <Box
        display="flex"
        padding="16px 0px"
        flexDirection="column"
        alignItems="flex-start"
        gap="8px"
        alignSelf="stretch"
        borderRadius="8px"
        border="1px solid #EBEFF3"
        background="#FDFDFD"
      >
        <Box display="flex" alignItems="flex-start" alignSelf="stretch">
          <Box width="50%">
            <OKRAchievedValue
              achieved={localCheckInStateValues?.actual !== undefined ? localCheckInStateValues?.actual : actual}
              onChange={(newActual) => handleLocalSave({ actual: newActual })}
              measureType={measureType}
              isReadOnly={cannotEditAchieved || isOKRPanelDisabled}
              readOnlyReason={achievedReadOnlyReason}
              labelProps={{
                width: 'calc(100%)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              containerProps={{ width: '100%' }}
            />
          </Box>
          <Box flex={1}>
            <OKRStatus
              goalClosureStatus={localCheckInStateValues?.goalClosureStatus || goalClosureStatus}
              goalProgressLevel={localCheckInStateValues?.goalProgressLevel || goalProgressLevel}
              onChange={handleLocalSave}
              isReadOnly={cannotEditAchieved || isOKRPanelDisabled}
            />
          </Box>
        </Box>
        <Box width="100%">
          <OKRProgressSlider
            goalProgressLevel={localCheckInStateValues?.goalProgressLevel || goalProgressLevel}
            goalClosureStatus={localCheckInStateValues?.goalClosureStatus || goalClosureStatus}
            startValue={startingProgressValue}
            achievedValue={localCheckInStateValues?.actual !== undefined ? localCheckInStateValues?.actual : actual}
            targetValue={target}
            measure={measure}
            completionPercentage={
              localCheckInStateValues?.avgProgressPercentage !== undefined
                ? localCheckInStateValues?.avgProgressPercentage
                : avgProgressPercentage
            }
            onChange={(newActual) => handleLocalSave({ actual: newActual })}
            measureType={measureType}
            isReadOnly={cannotEditAchieved || isStartAndTargetValueSame || isOKRPanelDisabled}
          />
        </Box>
        <Box
          width="100%"
          sx={{
            '.editorContainer': {
              minHeight: '44px',
              padding: '12px 12px 0px 12px',
            },
            '.DraftEditor-editorContainer': {
              maxHeight: '13.8rem !important',
            },
          }}
          paddingLeft="0.75rem"
          paddingRight="0.75rem"
        >
          <CommentSection
            key={uuid}
            taskId={uuid}
            taskType={TASK_TYPES_2.task}
            addCommentFromOutsideToggle={addCommentFromOutsideToggle}
            setAddCommentFromOutsideToggle={setAddCommentFromOutsideToggle}
            handleCommentSaved={handleCommentSaved}
            setIsCommentUpdateInProgress={setIsCommentUpdateInProgress}
            isCommentUpdateInProgress={isCommentUpdateInProgress}
            isEnterCommentAlwaysActive
            allowEnterToNextLine
            parentSource="CheckIn Panel"
            isCommentAllowedWithEnterButton={false}
            hideAvatar
            placeholderText="Enter comments here..."
            contentState={localCheckInStateValues?.contentState || null}
            media={localCheckInStateValues?.media || []}
            visibility={localCheckInStateValues?.visibility || null}
            sentiment={localCheckInStateValues?.sentiment || null}
            setModifiedCommentState={(newContentState, media, visibility, sentiment, isCommentExist) => {
              if (
                newContentState !== localCheckInStateValues?.contentState ||
                isCommentExist !== isCommentModified ||
                media.length !== localCheckInStateValues?.media?.length ||
                visibility !== localCheckInStateValues?.visibility ||
                sentiment !== localCheckInStateValues?.sentiment
              ) {
                handleLocalSave({
                  isCommentModified: isCommentExist,
                  contentState: newContentState,
                  visibility,
                  sentiment,
                  media,
                });
              }
            }}
            isCommentAllowed={canUpdateOKRStatusAndProgress}
          />
        </Box>
        <Box
          display="flex"
          padding="2px 0px"
          justifyContent="space-between"
          alignItems="center"
          alignSelf="stretch"
          paddingLeft="0.75rem"
          paddingRight="0.75rem"
        >
          <Box display="flex">
            <InfoWorkZero />
            <Box display="flex" flexDir="column">
              <TextLabel color="text.g2">
                Last check-in: {lastCheckInTS ? getHumanReadableDate(lastCheckInTS) : 'never'}
              </TextLabel>
              {checkCheckInValuesModified(localCheckInStateValues, okrDetails) || isCommentModified ? (
                <TextLabel color="#AF5C20">{`Click 'Check-in' to save your changes`}</TextLabel>
              ) : null}{' '}
            </Box>
          </Box>

          {canUpdateOKRStatusAndProgress ? (
            <Button
              variant="filled"
              style={{ height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={onCheckinClick}
              isDisabled={
                !(checkCheckInValuesModified(localCheckInStateValues, okrDetails) || isCommentModified) ||
                isCommentUpdateInProgress ||
                isOKRPanelDisabled
              }
            >
              {isCommentUpdateInProgress ? (
                <span style={{ paddingRight: '5px' }}>
                  <Spinner size="xs" color="white" />
                </span>
              ) : null}
              Check-in
            </Button>
          ) : null}
        </Box>
      </Box>
    </Flex>
  );
};

export default CheckIn;

export const RefableCheckIn = forwardRef((props, ref) => (
  <Box ref={ref} w="100%">
    <CheckIn {...props} />
  </Box>
));
